import { Link } from "react-router-dom";
import {
  ArrowRight,
  MagnifyingGlass,
  Users,
  ChartLineUp,
  ShieldCheck,
  Briefcase,
  ChatCircleText,
  ListChecks,
  Handshake,
} from "@phosphor-icons/react";

export default function Candidates() {
  return (
    <div data-testid="candidates-page">
      {/* HERO */}
      <section className="relative bg-navy-deep text-white overflow-hidden noise-overlay">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-28">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            <div className="lg:col-span-7">
              <div className="inline-flex items-center gap-2 px-5 py-2 rounded-full border border-white/15">
                <span className="eyebrow text-purple-accent">Candidats</span>
              </div>
              <h1 className="mt-8 font-serif text-4xl sm:text-5xl lg:text-6xl leading-[1.1] tracking-tight">
                Trouvez l'opportunité <br />
                qui révèle{" "}
                <span className="font-serif-italic text-purple-accent">votre potentiel</span>
              </h1>
              <div className="accent-line mt-8" />
              <p className="mt-8 text-white/75 max-w-xl leading-relaxed text-[16px]">
                Chez MP Advisory, nous accompagnons les talents Sales et IT en les
                connectant à des opportunités qui correspondent à leur profil,
                leurs ambitions et leurs objectifs de carrière. Bénéficiez d'un
                accompagnement personnalisé, confidentiel et humain à chaque étape
                de votre recherche.
              </p>
              <div className="mt-10 flex flex-wrap gap-4">
                <Link
                  to="/formulaire"
                  data-testid="candidates-cta-apply"
                  className="group inline-flex items-center gap-3 rounded-full bg-purple-accent hover:bg-purple-accent-hover text-white px-8 py-4 text-[15px] font-medium transition-all hover:-translate-y-0.5 shadow-purple-glow"
                >
                  Déposer ma candidature
                  <ArrowRight size={18} weight="bold" className="transition-transform group-hover:translate-x-1" />
                </Link>
                <Link
                  to="/contact"
                  data-testid="candidates-cta-contact"
                  className="inline-flex items-center gap-2 rounded-full border border-white/30 hover:border-white hover:bg-white/5 text-white px-8 py-4 text-[15px] font-medium transition-all"
                >
                  Échanger avec un consultant
                </Link>
              </div>
            </div>

            <div className="lg:col-span-5 relative">
              <div className="relative aspect-[4/5] rounded-3xl overflow-hidden ring-1 ring-white/10">
                <img
                  src="https://images.unsplash.com/photo-1758691737158-18ffa31c0a46?crop=entropy&cs=srgb&fm=jpg&q=85&w=900"
                  alt="Talents accompagnés"
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-navy-deep/50 via-transparent to-transparent" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FEATURE BLOCKS */}
      <section className="bg-body-bg py-24 lg:py-32" data-testid="candidates-features">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mb-16">
            <p className="eyebrow text-purple-accent">Ce que nous offrons</p>
            <h2 className="mt-4 font-serif text-4xl sm:text-5xl text-navy-deep leading-[1.1]">
              Un accompagnement <span className="font-serif-italic text-purple-accent">à votre image</span>
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { icon: MagnifyingGlass, title: "Opportunités ciblées", desc: "Des offres sélectionnées en fonction de votre profil, votre expérience et vos objectifs." },
              { icon: Users, title: "Accompagnement dédié", desc: "Un échange personnalisé pour comprendre vos attentes et vous orienter vers les bonnes organisations." },
              { icon: ChartLineUp, title: "Conseils & ressources", desc: "CV, entretiens, positionnement, prétentions : nous vous aidons à valoriser votre candidature." },
              { icon: ShieldCheck, title: "Confidentialité garantie", desc: "Votre démarche reste confidentielle, vos données sont traitées avec soin." },
            ].map((f, i) => (
              <article
                key={i}
                data-testid={`candidates-feature-${i}`}
                className="bg-white rounded-2xl p-8 border border-gray-100 shadow-subtle lift-on-hover"
              >
                <div className="w-12 h-12 rounded-xl bg-purple-soft flex items-center justify-center">
                  <f.icon size={24} weight="duotone" className="text-purple-accent" />
                </div>
                <h3 className="mt-6 font-serif text-xl text-navy-deep">{f.title}</h3>
                <p className="mt-3 text-slate-600 text-[14px] leading-relaxed">{f.desc}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* JOURNEY */}
      <section className="bg-white py-24 lg:py-32" data-testid="candidates-journey">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-20">
            <p className="eyebrow text-purple-accent">Votre parcours avec MP Advisory</p>
            <h2 className="mt-4 font-serif text-4xl sm:text-5xl text-navy-deep leading-[1.1]">
              Un accompagnement <span className="font-serif-italic text-purple-accent">à chaque étape</span>
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 relative">
            {/* Connecting dotted line */}
            <div className="hidden lg:block absolute top-8 left-[12.5%] right-[12.5%] border-t-2 border-dashed border-purple-accent/30" />

            {[
              { icon: Briefcase, num: "01", title: "Candidature", desc: "Postulez en quelques clics ou envoyez-nous votre profil de manière spontanée." },
              { icon: ChatCircleText, num: "02", title: "Échange", desc: "Nous prenons le temps de comprendre votre parcours, vos attentes et vos priorités." },
              { icon: ListChecks, num: "03", title: "Sélection", desc: "Nous vous présentons les opportunités les plus adaptées à vos objectifs de carrière." },
              { icon: Handshake, num: "04", title: "Intégration", desc: "Nous restons à vos côtés jusqu'à votre prise de poste et au-delà." },
            ].map((s, i) => (
              <div key={i} data-testid={`journey-step-${i}`} className="relative">
                <div className="bg-body-bg rounded-2xl p-8 border border-gray-200 relative z-10 h-full">
                  <div className="flex items-center justify-between">
                    <div className="w-16 h-16 rounded-2xl bg-purple-accent/10 flex items-center justify-center">
                      <s.icon size={28} weight="duotone" className="text-purple-accent" />
                    </div>
                    <span className="step-number text-5xl text-purple-accent/30">{s.num}</span>
                  </div>
                  <h3 className="mt-6 font-serif text-2xl text-navy-deep">{s.title}</h3>
                  <p className="mt-3 text-slate-600 text-[14px] leading-relaxed">{s.desc}</p>
                </div>
              </div>
            ))}
          </div>

          <div className="mt-16 text-center">
            <Link
              to="/formulaire"
              data-testid="journey-cta"
              className="inline-flex items-center gap-2 rounded-full bg-navy-deep hover:bg-navy-light text-white px-8 py-4 text-sm font-medium transition-all hover:-translate-y-0.5"
            >
              Démarrer mon parcours
              <ArrowRight size={16} weight="bold" />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
